import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, Trophy } from 'lucide-react';
import type { CustomerTierRuleToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { serverMessage } from '~/lib/api/server-error';
import { useCustomerTiersList } from '~/hooks/use-customer-tiers';
import { useCustomerTierCriteriaTypes, useCustomerTierRulesList, useCustomerTierRuleMutations } from '~/hooks/use-customer-tier-rules';
import { TierRuleEditDialog } from './tier-rule-edit-dialog';

const Bool = ({ on }: { on: boolean }) =>
	on ? <Check size={16} className="text-success-fg" /> : <span className="text-foreground-disabled">—</span>;

/** Customer tier rules (Κανόνες επιπέδων) — thresholds that promote customers between loyalty tiers. */
export function TierRulesGridView() {
	const { t } = useTranslation();
	const { data, isLoading, isError, refetch } = useCustomerTierRulesList();
	const { remove } = useCustomerTierRuleMutations();
	const { data: tiers } = useCustomerTiersList();
	const { data: criteriaTypes } = useCustomerTierCriteriaTypes();

	const [editRow, setEditRow] = useState<CustomerTierRuleToken | null>(null);
	const [editOpen, setEditOpen] = useState(false);
	const [deleteRow, setDeleteRow] = useState<CustomerTierRuleToken | null>(null);

	const tierName = useMemo(() => new Map((tiers ?? []).map(tier => [tier.id, tier.nm ?? ''])), [tiers]);
	const criteriaName = useMemo(() => new Map((criteriaTypes ?? []).map(c => [c.id, c.nm ?? ''])), [criteriaTypes]);
	const fld = (k: string) => t(`loyalty.tierRules.fields.${k}`);

	const columns = useMemo<SystemGridColumn<CustomerTierRuleToken>[]>(
		() => [
			{
				key: 'tier',
				header: fld('tier'),
				width: 'minmax(0,1fr)',
				sortValue: o => tierName.get(o.c_tier_id) ?? '',
				exportValue: o => tierName.get(o.c_tier_id) ?? '',
				render: o => <span className="truncate text-sm font-medium text-foreground">{tierName.get(o.c_tier_id) || `#${o.c_tier_id}`}</span>
			},
			{
				key: 'criteriaType',
				header: fld('criteriaType'),
				width: 'minmax(0,1fr)',
				sortValue: o => criteriaName.get(o.c_tier_ctype as number) ?? '',
				exportValue: o => criteriaName.get(o.c_tier_ctype as number) ?? '',
				render: o => <span className="truncate text-sm text-foreground-muted">{criteriaName.get(o.c_tier_ctype as number) || '—'}</span>
			},
			{
				key: 'value',
				header: fld('value'),
				width: '140px',
				align: 'right',
				sortValue: o => o.c_tier_cvalue ?? 0,
				exportValue: o => o.c_tier_cvalue ?? 0,
				render: o => <span className="text-sm tabular-nums text-foreground-muted">{(o.c_tier_cvalue ?? 0).toLocaleString('el-GR')}</span>
			},
			{
				key: 'timeFrame',
				header: fld('timeFrameMonth'),
				width: '140px',
				align: 'right',
				sortValue: o => o.time_frame_month ?? 0,
				exportValue: o => o.time_frame_month ?? 0,
				render: o => <span className="text-sm tabular-nums text-foreground-muted">{o.time_frame_month ?? '—'}</span>
			},
			{
				key: 'description',
				header: fld('description'),
				width: 'minmax(0,1.4fr)',
				defaultVisible: false,
				sortValue: o => o.descr ?? '',
				exportValue: o => o.descr ?? '',
				render: o => <span className="truncate text-sm text-foreground-muted">{o.descr || '—'}</span>
			},
			{
				key: 'active',
				header: fld('active'),
				width: '90px',
				align: 'center',
				sortValue: o => (o.act ? 1 : 0),
				exportValue: o => (o.act ? t('common.yes') : t('common.no')),
				render: o => <Bool on={Boolean(o.act)} />
			}
		],
		[t, tierName, criteriaName]
	);

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteRow(null);
			},
			onError: (err: unknown) => toast.error(serverMessage(err))
		});
	};

	return (
		<>
			<SystemGrid<CustomerTierRuleToken>
				gridId="customer-tier-rules"
				title={t('loyalty.tierRules.title')}
				rows={data ?? []}
				loading={isLoading}
				error={isError}
				onRetry={() => void refetch()}
				getRowId={o => o.id ?? `${o.c_tier_id}-${o.c_tier_ctype}`}
				columns={columns}
				searchPredicate={(o, q) =>
					(tierName.get(o.c_tier_id) ?? '').toLowerCase().includes(q) ||
					(criteriaName.get(o.c_tier_ctype as number) ?? '').toLowerCase().includes(q) ||
					(o.descr ?? '').toLowerCase().includes(q)
				}
				defaultSort={{ key: 'tier', dir: 'asc' }}
				emptyIcon={<Trophy size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
				addLabel={t('loyalty.tierRules.add')}
				onAdd={() => {
					setEditRow(null);
					setEditOpen(true);
				}}
				onEdit={o => {
					setEditRow(o);
					setEditOpen(true);
				}}
				onDelete={setDeleteRow}
				exportFilename="customer-tier-rules"
			/>

			<TierRuleEditDialog open={editOpen} onOpenChange={setEditOpen} row={editRow} />

			<Dialog open={Boolean(deleteRow)} onOpenChange={open => !open && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('loyalty.tierRules.deleteTitle')}</DialogTitle>
						<DialogDescription>
							{t('loyalty.tierRules.deleteBody', { name: deleteRow ? (tierName.get(deleteRow.c_tier_id) ?? '') : '' })}
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
